import { useLocation } from '@tanstack/react-router';
import { useAuth } from '_/contexts/AuthContext';
import fullLogo from '_/full-logo.svg';
import { endpoints } from '_/lib/frontend-utils';
import { Button } from 'antd';
import React, { useCallback, useEffect, useMemo } from 'react';

import classes from './Login.module.css';

const redirectParamName = 'redirectUrl';

const getCurrentHost = () => {
  const { hostname, port, protocol } = window.location;
  return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
};

const Login = (): JSX.Element => {
  const { currentUser, isLoading } = useAuth();
  const { search } = useLocation();

  const redirectUrl = useMemo(() => {
    const url = new URLSearchParams(search).get(redirectParamName) ?? '';
    return url.startsWith('https://') ? url : `${getCurrentHost()}/orgs`;
  }, [search]);

  useEffect(() => {
    // Only redirect if we're done loading and have a user
    if (!isLoading && currentUser) {
      window.location.assign(redirectUrl);
    }
  }, [currentUser, isLoading, redirectUrl]);

  const handleSsoLoginClick = useCallback(() => {
    window.location.assign(`${endpoints.workbenchApiBaseUrl}/login/sso?${redirectParamName}=${redirectUrl}`);
  }, [redirectUrl]);

  const handleGithubLoginClick = useCallback(() => {
    window.location.assign(`${endpoints.workbenchApiBaseUrl}/login?${redirectParamName}=${redirectUrl}`);
  }, [redirectUrl]);

  return (
    <div className={classes.loginContainer}>
      <img alt="Import.io logo" className={classes.logo} src={fullLogo} />
      <div className={classes.loginCard}>
        <div className={classes.loginContent}>
          <h1 className={classes.title}>Welcome to Workbench</h1>
          <p className={classes.subtitle}>Sign in to continue</p>
          <div className={classes.buttonGroup}>
            <Button type="primary" size="large" className={classes.loginButton} onClick={handleSsoLoginClick}>
              Sign in with SSO
            </Button>
            <Button size="large" className={classes.loginButton} onClick={handleGithubLoginClick}>
              Continue with GitHub
            </Button>
          </div>
          <p className={classes.termsText}>
            By logging in, you agree to our{' '}
            <a href="https://www.import.io/terms-conditions/" target="_blank" rel="noreferrer" className={classes.link}>
              Terms & Conditions
            </a>{' '}
            and{' '}
            <a href="https://www.import.io/privacy/" target="_blank" rel="noreferrer" className={classes.link}>
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
